import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Plus, RefreshCw, Star, Eye, EyeOff, Trash2, X } from "lucide-react";
import { contentApi } from "./api";
import type { Content, ContentInput, ContentType, RelatedOption } from "./types";
import { STATE_LABELS, TYPE_LABELS, dateTimeLabel, sourceFieldFor, stateOf } from "./labels";
import { ScheduleDialog } from "./ScheduleDialog";
import { CoverField } from "./editor/CoverField";
import { RelatedPicker } from "./editor/RelatedPicker";
import { PhonePreview } from "./editor/PhonePreview";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "draft", label: "Borradores" },
  { value: "scheduled", label: "Programados" },
  { value: "published", label: "Publicados" },
  { value: "archived", label: "Archivados" },
];

const EMPTY_FORM: ContentInput = {
  type: "NEWS",
  title: "",
  excerpt: "",
  coverUrl: "",
  body: "",
  sourceUrl: "",
  authorType: "EDITORIAL",
  genreTags: [],
  cityTags: [],
};

function tagsToInput(tags: string[]): string {
  return (tags || []).join(", ");
}
function inputToTags(value: string): string[] {
  return value
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

export function ContentScreen() {
  const qc = useQueryClient();
  const { push } = useToast();
  const canManage = usePermission(PERMISSIONS.CONTENT_MANAGE);
  const canPublish = usePermission(PERMISSIONS.CONTENT_PUBLISH);

  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState<Content | "new" | null>(null);
  const [scheduling, setScheduling] = useState<Content | null>(null);

  const listQuery = useQuery({
    queryKey: ["content", "list", status],
    queryFn: () => contentApi.list({ status: status || undefined }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["content"] });

  const publishMut = useMutation({
    mutationFn: ({ id, publish }: { id: number; publish: boolean }) =>
      contentApi.publish(id, publish),
    onSuccess: (_d, v) => {
      push({ kind: "success", message: v.publish ? "Publicado." : "Volvió a borrador." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo cambiar el estado." }),
  });

  const scheduleMut = useMutation({
    mutationFn: ({ id, publishAt }: { id: number; publishAt: string }) =>
      contentApi.publish(id, true, publishAt),
    onSuccess: (data) => {
      // Un backend sin programación (antes de backend#115) publica de inmediato.
      if (stateOf(data) !== "scheduled") {
        push({ kind: "error", message: "El servidor todavía no programa: la pieza quedó publicada ahora." });
      } else {
        push({ kind: "success", message: `Programado para el ${dateTimeLabel(data.publishedAt)}` });
      }
      setScheduling(null);
      invalidate();
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      push({ kind: "error", message: msg || "No se pudo programar." });
    },
  });

  const featureMut = useMutation({
    mutationFn: ({ id, days }: { id: number; days: number }) => contentApi.feature(id, days),
    onSuccess: (_d, v) => {
      push({ kind: "success", message: v.days > 0 ? "Destacado." : "Destaque quitado." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo destacar." }),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => contentApi.remove(id),
    onSuccess: () => {
      push({ kind: "success", message: "Contenido eliminado." });
      invalidate();
    },
    onError: () => push({ kind: "error", message: "No se pudo eliminar." }),
  });

  const rows = listQuery.data ?? [];

  const isFeatured = (c: Content) =>
    !!c.featuredUntil && new Date(c.featuredUntil).getTime() > Date.now();

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Cultura · Revista Mixcore</p>
          <h1 className="nx-h1">Contenido editorial</h1>
          <p className="text-sm text-fg-muted mt-1">
            Lanzamientos, sets, videos, entrevistas, noticias, reportajes y la agenda de la semana.
            Lo publicado aparece en la app (pestaña Cultura) y en mixcore.club/comunidad.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => listQuery.refetch()}
            className="nx-btn-secondary"
            disabled={listQuery.isFetching}
          >
            <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
            Actualizar
          </button>
          {canManage && (
            <button onClick={() => setEditing("new")} className="nx-btn-primary">
              <Plus size={14} /> Nuevo contenido
            </button>
          )}
        </div>
      </header>

      <div className="flex items-center gap-1.5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatus(f.value)}
            className={
              "px-3 py-1.5 rounded-md text-xs font-medium transition-colors " +
              (status === f.value
                ? "bg-accent text-fg-inverted"
                : "bg-bg-subtle text-fg-muted hover:text-fg")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {listQuery.isLoading ? (
        <div className="text-sm text-fg-subtle py-10 text-center">Cargando contenido…</div>
      ) : listQuery.isError ? (
        <div className="text-sm text-danger py-10 text-center">
          Error al cargar.{" "}
          <button className="underline" onClick={() => listQuery.refetch()}>
            Reintentar
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-border-subtle rounded-lg py-12 text-center">
          <p className="text-sm text-fg-muted">Todavía no hay contenido.</p>
          {canManage && (
            <button onClick={() => setEditing("new")} className="nx-btn-secondary mt-3">
              <Plus size={14} /> Crear el primero
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto border border-border-subtle rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle bg-bg-subtle text-2xs uppercase tracking-wide text-fg-subtle">
                <th className="text-left font-semibold px-3 py-2">Título</th>
                <th className="text-left font-semibold px-3 py-2">Tipo</th>
                <th className="text-left font-semibold px-3 py-2">Estado</th>
                <th className="text-left font-semibold px-3 py-2">Tags</th>
                <th className="text-right font-semibold px-3 py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-border-subtle last:border-0 hover:bg-bg-hover"
                >
                  <td className="px-3 py-2">
                    <button
                      className="font-medium text-fg hover:text-accent text-left"
                      onClick={() => setEditing(c)}
                    >
                      {c.title}
                    </button>
                    {isFeatured(c) && (
                      <span className="ml-2 inline-flex items-center gap-1 text-2xs text-accent">
                        <Star size={11} /> Destacado
                      </span>
                    )}
                    <div className="text-2xs text-fg-subtle">/{c.slug}</div>
                  </td>
                  <td className="px-3 py-2 text-fg-muted text-xs">{TYPE_LABELS[c.type] ?? c.type}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        "inline-flex text-2xs rounded px-1.5 py-0.5 border " +
                        (stateOf(c) === "published"
                          ? "border-success text-success"
                          : stateOf(c) === "scheduled"
                          ? "border-info text-info"
                          : stateOf(c) === "draft"
                          ? "border-border-subtle text-fg-muted"
                          : "border-border-subtle text-fg-subtle")
                      }
                    >
                      {STATE_LABELS[stateOf(c)]}
                    </span>
                    {stateOf(c) === "scheduled" && (
                      <div className="text-2xs text-fg-subtle mt-0.5">{dateTimeLabel(c.publishedAt)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {[...(c.genreTags || []), ...(c.cityTags || [])].slice(0, 4).map((t) => (
                        <span
                          key={t}
                          className="inline-flex text-2xs bg-bg-subtle border border-border-subtle text-fg-muted rounded px-1.5 py-0.5"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                      {canPublish && (
                        <>
                          {stateOf(c) !== "published" && (
                            <button
                              title="Publicar ahora"
                              className="p-1.5 rounded hover:bg-bg-subtle text-fg-muted hover:text-fg"
                              disabled={publishMut.isPending}
                              onClick={() => publishMut.mutate({ id: c.id, publish: true })}
                            >
                              <Eye size={15} />
                            </button>
                          )}
                          {stateOf(c) !== "published" && (
                            <button
                              title={stateOf(c) === "scheduled" ? "Cambiar la fecha" : "Programar"}
                              className={
                                "p-1.5 rounded hover:bg-bg-subtle " +
                                (stateOf(c) === "scheduled" ? "text-info" : "text-fg-muted hover:text-fg")
                              }
                              onClick={() => setScheduling(c)}
                            >
                              <CalendarClock size={15} />
                            </button>
                          )}
                          {stateOf(c) !== "draft" && (
                            <button
                              title={stateOf(c) === "scheduled" ? "Volver a borrador" : "Despublicar"}
                              className="p-1.5 rounded hover:bg-bg-subtle text-fg-muted hover:text-fg"
                              disabled={publishMut.isPending}
                              onClick={() => publishMut.mutate({ id: c.id, publish: false })}
                            >
                              <EyeOff size={15} />
                            </button>
                          )}
                          <button
                            title={isFeatured(c) ? "Quitar destaque" : "Destacar 7 días"}
                            className={
                              "p-1.5 rounded hover:bg-bg-subtle " +
                              (isFeatured(c) ? "text-accent" : "text-fg-muted hover:text-fg")
                            }
                            disabled={featureMut.isPending}
                            onClick={() =>
                              featureMut.mutate({ id: c.id, days: isFeatured(c) ? 0 : 7 })
                            }
                          >
                            <Star size={15} />
                          </button>
                        </>
                      )}
                      {canManage && (
                        <button
                          title="Eliminar"
                          className="p-1.5 rounded hover:bg-bg-subtle text-fg-muted hover:text-danger"
                          disabled={removeMut.isPending}
                          onClick={async () => {
                            const ok = await confirmAction({
                              title: "Eliminar contenido",
                              message: `"${c.title}" se eliminará del feed.`,
                              confirmLabel: "Eliminar",
                              tone: "danger",
                            });
                            if (ok === true) removeMut.mutate(c.id);
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {scheduling && canPublish && (
        <ScheduleDialog
          item={scheduling}
          busy={scheduleMut.isPending}
          onClose={() => setScheduling(null)}
          onConfirm={(publishAt) => scheduleMut.mutate({ id: scheduling.id, publishAt })}
        />
      )}

      {editing !== null && canManage && (
        <ContentEditor
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function ContentEditor({
  item,
  onClose,
  onSaved,
}: {
  item: Content | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push } = useToast();
  const [form, setForm] = useState<ContentInput>(() =>
    item
      ? {
          type: item.type,
          title: item.title,
          excerpt: item.excerpt ?? "",
          coverUrl: item.coverUrl ?? "",
          body: item.body ?? "",
          sourceUrl: item.sourceUrl ?? "",
          authorType: item.authorType,
          genreTags: item.genreTags ?? [],
          cityTags: item.cityTags ?? [],
          relatedArtistaId: item.relatedArtistaId,
          relatedEventId: item.relatedEventId,
          relatedClubId: item.relatedClubId,
        }
      : { ...EMPTY_FORM },
  );
  const [related, setRelated] = useState<{ artist: RelatedOption | null; event: RelatedOption | null; club: RelatedOption | null }>({
    artist: null,
    event: null,
    club: null,
  });
  const source = sourceFieldFor(form.type);
  const [genreInput, setGenreInput] = useState(tagsToInput(item?.genreTags ?? []));
  const [cityInput, setCityInput] = useState(tagsToInput(item?.cityTags ?? []));

  const set = (patch: Partial<ContentInput>) => setForm((f) => ({ ...f, ...patch }));

  const saveMut = useMutation({
    mutationFn: () => {
      const payload: ContentInput = {
        ...form,
        genreTags: inputToTags(genreInput),
        cityTags: inputToTags(cityInput),
      };
      return item ? contentApi.update(item.id, payload) : contentApi.create(payload);
    },
    onSuccess: () => {
      push({ kind: "success", message: item ? "Cambios guardados." : "Contenido creado (borrador)." });
      onSaved();
    },
    onError: () => push({ kind: "error", message: "No se pudo guardar. Revisa los campos." }),
  });

  const canSave = form.title.trim().length >= 3;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 py-10">
      <div className="w-full max-w-6xl rounded-lg bg-bg-base border border-border-subtle shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-3">
          <h2 className="text-base font-semibold text-fg">
            {item ? "Editar contenido" : "Nuevo contenido"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded text-fg-muted hover:bg-bg-subtle hover:text-fg">
            <X size={18} />
          </button>
        </div>

        <div className="grid gap-6 px-5 py-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Tipo">
              <select
                className="nx-input"
                value={form.type}
                onChange={(e) => set({ type: e.target.value as ContentType })}
              >
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Labeled>
            <Labeled label="Autoría">
              <select
                className="nx-input"
                value={form.authorType}
                onChange={(e) => set({ authorType: e.target.value as ContentInput["authorType"] })}
              >
                <option value="EDITORIAL">Editorial Mixcore</option>
                <option value="ARTIST">Artista</option>
                <option value="PRODUCER">Productor</option>
              </select>
            </Labeled>
          </div>

          <Labeled label="Título">
            <input
              className="nx-input"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Título del contenido"
              maxLength={200}
            />
          </Labeled>

          <Labeled label="Bajada / excerpt">
            <input
              className="nx-input"
              value={form.excerpt ?? ""}
              onChange={(e) => set({ excerpt: e.target.value })}
              placeholder="Resumen corto que se ve en la card"
              maxLength={320}
            />
          </Labeled>

          <CoverField value={form.coverUrl ?? ""} onChange={(coverUrl) => set({ coverUrl })} />

          <Labeled label="Cuerpo (markdown)">
            <textarea
              className="nx-input min-h-[160px] font-mono text-xs"
              value={form.body ?? ""}
              onChange={(e) => set({ body: e.target.value })}
              placeholder="Texto en markdown: ## subtítulo, **negrita**, *cursiva*, - listas, > citas y [enlaces](https://…)"
            />
          </Labeled>

          <Labeled label={source.label}>
            <input
              className="nx-input"
              value={form.sourceUrl ?? ""}
              onChange={(e) => set({ sourceUrl: e.target.value })}
              placeholder={source.placeholder}
            />
          </Labeled>

          <div className="grid grid-cols-2 gap-3">
            <Labeled label="Géneros (separados por coma)">
              <input
                className="nx-input"
                value={genreInput}
                onChange={(e) => setGenreInput(e.target.value)}
                placeholder="Techno, House"
              />
            </Labeled>
            <Labeled label="Ciudades (separadas por coma)">
              <input
                className="nx-input"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                placeholder="Santiago, Valparaíso"
              />
            </Labeled>
          </div>

          <div className="flex flex-col gap-3 rounded-md border border-border-subtle p-3">
            <p className="text-xs text-fg-muted">
              ¿De quién o de qué habla? En la app aparece en «En Mixcore» y lleva a su página.
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              <RelatedPicker
                kind="artist"
                label="Artista"
                value={form.relatedArtistaId}
                onChange={(id) => set({ relatedArtistaId: id })}
                onResolved={(o) => setRelated((r) => ({ ...r, artist: o }))}
              />
              <RelatedPicker
                kind="event"
                label="Evento"
                value={form.relatedEventId}
                onChange={(id) => set({ relatedEventId: id })}
                onResolved={(o) => setRelated((r) => ({ ...r, event: o }))}
              />
              <RelatedPicker
                kind="club"
                label="Club"
                value={form.relatedClubId}
                onChange={(id) => set({ relatedClubId: id })}
                onResolved={(o) => setRelated((r) => ({ ...r, club: o }))}
              />
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-4 self-start">
          <PhonePreview
            type={form.type}
            title={form.title}
            excerpt={form.excerpt ?? ""}
            coverUrl={form.coverUrl ?? ""}
            body={form.body ?? ""}
            sourceUrl={form.sourceUrl ?? ""}
            publishedAt={item?.publishedAt ?? null}
            related={related}
          />
        </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-3">
          <button onClick={onClose} className="nx-btn-secondary">
            Cancelar
          </button>
          <button
            onClick={() => saveMut.mutate()}
            disabled={!canSave || saveMut.isPending}
            className="nx-btn-primary"
          >
            {saveMut.isPending ? "Guardando…" : item ? "Guardar cambios" : "Crear borrador"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-fg-muted">{label}</span>
      {children}
    </label>
  );
}
